import { DELETE_MANY_LINE_ITEMS_MUTATION } from "@/apis/gql/apolloClient";
import { type DocumentNode, useMutation, useQuery } from "@apollo/client";

import { INVOICE_QUERY } from "../invoices/get";

import { LINE_ITEMS_QUERY } from "./fetch";

export const useDeleteLineItems = (
  tenantId: string,
  invoiceId: string,
  options?: {
    refetchQueries?: DocumentNode[];
  },
) => {
  const [deleteManyLineItems] = useMutation(DELETE_MANY_LINE_ITEMS_MUTATION, {
    refetchQueries: options?.refetchQueries,
  });

  const { refetch } = useQuery(INVOICE_QUERY, {
    variables: { invoiceId: invoiceId, tenantId },
  });

  return async (lineItemIds: string[], invoiceId: string | undefined) => {
    if (!invoiceId) {
      throw new Error("Invoice ID is required");
    }

    await deleteManyLineItems({
      variables: { lineItemIds },
      optimisticResponse: {
        __typename: "Mutation",
        deleteManyLineItems: lineItemIds,
      },
      update(cache, { data: { deleteManyLineItems } }) {
        if (deleteManyLineItems.length !== lineItemIds.length) {
          refetch();
          return;
        }

        // Read the current data from the cache
        const data: any = cache.readQuery({
          query: LINE_ITEMS_QUERY,
          variables: { invoiceId, paranoid: true },
        });

        // Filter out the deleted line items from the cache
        const newLineItemDetails = data.lineItemDetails.filter((item: any) => {
          return !deleteManyLineItems.includes(item.lineItemDetailsId);
        });
        const newData = {
          lineItemDetails: newLineItemDetails,
        };

        // Write the updated data back to the cache
        cache.writeQuery({
          query: LINE_ITEMS_QUERY,
          data: newData,
          variables: { invoiceId, paranoid: true },
        });
      },
    });
  };
};
